import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, InputAdornment, Tooltip, styled, useMediaQuery, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import CustomNoRowsOverlay from '../Table/CustomNoRowsOverlay';
import { IColumn, IMemberRow } from './Columns';
import SearchInput from '../Common/SearchInput';
import LoadingSkeleton from '../ClientTable/LoadingSkeleton';
import AddTeamMemberModal from '../AddTeamMemberModal/AddTeamMemberModal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { closeAddMemberModalOnSuccess } from '../../redux/coachGroup/coachGroupSlice';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

export interface PaginationModelChangeProps {
  pageSize: number;
  page: number;
}

export interface CellClickProps {
  field: string;
  row: {
    id: string;
    avatar: string;
    name: string;
    firstName: string;
    lastName: string;
  };
}

const StyledButton = styled(Button)(() => ({
  padding: '12px 12px !important',
  gap: '10px',
  borderRadius: '10px',
  border: '2px solid var(--Brand-Cadet-Blue, #6599A5) !important',
  fontFamily: 'Satoshi',
  fontSize: '15px',
  fontWeight: 700,
  lineHeight: '20px ',
  minWidth: '100px',
  '& span': {
    minWidth: '100px',
    background: 'linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    whiteSpace: 'nowrap',
  },
}));

interface MemberTableProps {
  handleOnCellClick?: (_params: CellClickProps) => void;
  handleOnPaginationModelChange: (_event: PaginationModelChangeProps) => void;
  rows: IMemberRow[];
  rowCount: number;
  loading: boolean;
  columns: IColumn[];
  handleOnChangeSearchTerm: (_v: string) => void;
  searchTerm: string;
  pageSize: number;
  page: number;
  addExtraBottomMargin?: boolean;
  isArchived?: boolean;
  onAddSuccess: () => void;
}

export default function MemberTable({
  handleOnCellClick,
  handleOnPaginationModelChange,
  rows,
  rowCount,
  loading,
  columns,
  handleOnChangeSearchTerm,
  searchTerm,
  pageSize,
  page,
  addExtraBottomMargin = false,
  isArchived = false,
  onAddSuccess,
}: Readonly<MemberTableProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false);
  const getRowClassName = () => 'no-border';
  const dispatch = useAppDispatch();
  const { isPrincipalCoach } = useAppSelector((state) => state.coach.coach);

  const isAddMemberSuccess = useAppSelector((state) => state.coachGroup.isAddMemberSuccess);
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const height = addExtraBottomMargin ? 'max-content' : '100%';

  useEffect(() => {
    if (isAddMemberSuccess) {
      setIsOpen(false);
      dispatch(closeAddMemberModalOnSuccess());
      onAddSuccess();
    }
  }, [isAddMemberSuccess]);

  return (
    <Box
      height={height}
      display="flex"
      flexDirection="column"
      sx={{ bgcolor: '#FFF', boxShadow: theme.borderStyles.cardBorders, borderRadius: '10px' }}
    >
      <Box display="flex" flexDirection="row" p="20px" justifyContent="space-between" gap="4px">
        <Box width="100%" height="38px">
          <SearchInput
            type="text"
            placeholder={t('teamMembersTable.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => handleOnChangeSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src="/assets/icons/magnifer.svg" alt="search" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {!isArchived &&
          isPrincipalCoach &&
          checkPermissions(UserPermissionType.TEAM_MODULE_ADD_TEAM_MEMBER) && (
            <Box margin="0 4px 0 4px">
              <Box margin="auto" display="flex">
                <StyledButton variant="outlined" onClick={() => setIsOpen(true)}>
                  <Box component="img" src="/assets/icons/user-plus.svg" alt="plus-icon" />
                  {isSmallScreen ? (
                    <Tooltip title={t('teamMembersTable.addNewMember')}>
                      <span>{`${t('teamMembersTable.addNewMember').substring(0, 3)}...`}</span>
                    </Tooltip>
                  ) : (
                    <span>{t('teamMembersTable.addNewMember')}</span>
                  )}
                </StyledButton>
                {isOpen && <AddTeamMemberModal isOpen={isOpen} onClose={() => setIsOpen(false)} />}
              </Box>
            </Box>
          )}
      </Box>

      <Box
        flexGrow="1"
        width="100%"
        height="calc(100vh - 264px)"
        sx={{
          '&::-webkit-scrollbar': {
            width: '0px',
            background: 'transparent',
          },
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
        }}
      >
        <DataGrid
          onPaginationModelChange={handleOnPaginationModelChange}
          onCellClick={handleOnCellClick}
          rows={rows}
          rowCount={rowCount}
          paginationMode="server"
          columns={columns as GridColDef[]}
          getRowClassName={getRowClassName}
          columnVisibilityModel={{
            id: false,
          }}
          pageSizeOptions={[5, 10, 15]}
          disableRowSelectionOnClick
          columnHeaderHeight={44}
          loading={loading}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize,
                page,
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-paginationToolbar': {
              justifyContent: 'space-between',
            },
            borderRight: 'none',
            borderLeft: 'none',
            borderBottom: 'none',
            borderColor: '#7CAEBE',
            borderRadius: '0px',
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              paddingLeft: '30px',
              borderColor: 'primary.light',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: () => theme.palette.primary.main,
              fontWeight: 500,
            },
            '& .MuiDataGrid-columnHeader:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              paddingLeft: '20px',
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderColor: '#7CAEBE',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: '#7CAEBE',
            },
            '& .MuiTablePagination-root button': {
              mt: '4px',
              color: '#6CA3AC' /* Change text color */,
              '&:disabled': {
                opacity: 0.5,
              },
            },
            '& .MuiTablePagination-input': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
              ml: 0,
              pl: 0,
            },
            '& .MuiTablePagination-root .MuiTablePagination-selectLabel': {
              color: '#7CAEBE',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 400,
              lineHeight: '18px',
            },
            '& .MuiTablePagination-displayedRows': {
              color: 'primary.main',
              fontFamily: 'Satoshi',
              fontSize: '13px',
              fontWeight: 700,
              lineHeight: '18px',
              mt: '4px',
            },
            '& .MuiDataGrid-virtualScroller': {
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f5f5f5',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#ccc',
                borderRadius: '20px',
                border: '3px solid #f5f5f5',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: '#888',
              },
            },
          }}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            loadingOverlay: LoadingSkeleton,
          }}
        />
      </Box>
    </Box>
  );
}
